import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";

export interface ChecklistToSave {
  cid: string;
  rid: string;
  lbid: string;
  lbname?: string;
  yachtid: string;
  action?: string;
  item?: string;
  url: string;
  queuedAt: number;
}

export const checklistActionKey = (entry: ChecklistToSave) =>
  [entry.url, entry.cid, entry.rid, entry.action ?? "", entry.item ?? ""].join(
    "\u0000",
  );

const CHECKLIST_QUEUE_STORAGE_KEY = "pendingChecklistActions";
// An action whose entry never got saved has nothing to attach to.
const CHECKLIST_QUEUE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// The queue drains only on save, and offline the autosave never runs, so
// without this every tick since the last manual save is lost on restart.
const readPersistedChecklistQueue = (): ChecklistToSave[] => {
  try {
    const raw = localStorage.getItem(CHECKLIST_QUEUE_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.queue)) return [];

    const now = Date.now();
    return (parsed.queue as ChecklistToSave[]).filter(
      entry =>
        now - Number(entry.queuedAt ?? parsed.savedAt) <=
        CHECKLIST_QUEUE_MAX_AGE_MS,
    );
  } catch {
    return [];
  }
};

export const persistChecklistQueue = (queue: ChecklistToSave[]) => {
  try {
    if (queue.length === 0) {
      localStorage.removeItem(CHECKLIST_QUEUE_STORAGE_KEY);
      return;
    }
    localStorage.setItem(
      CHECKLIST_QUEUE_STORAGE_KEY,
      JSON.stringify({ savedAt: Date.now(), queue }),
    );
  } catch {
    // Storage full: the queue still works for this session.
  }
};

interface IEntrySlice {
  current: string;
  editing: boolean;
  activeAutosave: boolean;
  autosaveSuspendCount: number;
  silentEntryTransition: boolean;
  openedSaveModal: boolean;
  openedRefetchModal: boolean;
  isSaving: boolean;
  lastUpdate: string;
  locked: boolean;
  user: {
    full_name: string;
    rolename: string;
    userid: string;
  } | null;
  you: boolean;
  changed: number | null;
  newChecklistToSave: ChecklistToSave[];
  releaseEntryLock: number;
  noSaves: number;
  lockedByOther: boolean;
}

const initialState: IEntrySlice = {
  current: "",
  editing: false,
  isSaving: false,
  lastUpdate: "",
  activeAutosave: false,
  autosaveSuspendCount: 0,
  silentEntryTransition: false,
  openedSaveModal: false,
  openedRefetchModal: false,
  locked: false,
  user: null,
  you: false,
  changed: null,
  newChecklistToSave: readPersistedChecklistQueue(),
  releaseEntryLock: 0,
  noSaves: 0,
  lockedByOther: false,
};

export const entrySlice = createSlice({
  name: "entrySlice",
  initialState,
  reducers: {
    removeFlushedChecklistActions: (state, action: PayloadAction<string[]>) => {
      const flushed = new Set(action.payload);
      state.newChecklistToSave = state.newChecklistToSave.filter(
        el => !flushed.has(checklistActionKey(el)),
      );
      persistChecklistQueue(state.newChecklistToSave);
    },

    pushNewEntryActionsData: (
      state,
      action: { payload: Omit<ChecklistToSave, "queuedAt"> },
    ) => {
      // Scoped by cid+rid too: one item name on two checklists or entries is
      // two different actions.
      state.newChecklistToSave = state.newChecklistToSave.filter(
        el =>
          !(
            el.cid === action.payload.cid &&
            el.rid === action.payload.rid &&
            el.item === action.payload.item &&
            el.action === action.payload.action
          ),
      );
      state.newChecklistToSave.push({
        ...action.payload,
        queuedAt: Date.now(),
      });
      persistChecklistQueue(state.newChecklistToSave);
    },

    reassignNewChecklistActions: (
      state,
      action: PayloadAction<{
        realId: string;
        lbid?: string;
        yachtid?: string;
      }>,
    ) => {
      const { realId, lbid, yachtid } = action.payload;
      if (!realId || realId === "new") return;

      state.newChecklistToSave = state.newChecklistToSave.map(entry =>
        (!entry.rid || entry.rid === "new") &&
        (!lbid || entry.lbid === lbid) &&
        (!yachtid || entry.yachtid === yachtid)
          ? { ...entry, rid: realId }
          : entry,
      );
      persistChecklistQueue(state.newChecklistToSave);
    },

    initEntryLockData: (
      state,
      action: PayloadAction<IEntrySlice & { success?: boolean }>,
    ) => {
      delete action.payload.success;
      return { ...state, ...action.payload };
    },
    setEntryLockData: (
      state,
      action: PayloadAction<any & { success?: boolean }>,
    ) => {
      delete action.payload.success;
      const { activeAutosave, locked, user, you, changed, lockedByOther } =
        action.payload;

      if (Boolean(lockedByOther) !== state.lockedByOther) {
        state.lockedByOther = lockedByOther;
      }

      state.activeAutosave = activeAutosave;
      state.locked = locked;
      state.user = user;
      state.you = you;
      state.changed = changed;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.editing = action.payload;
    },
    setAutoSaving: (
      state,
      action: PayloadAction<{ isSaving: boolean; isError?: boolean }>,
    ) => {
      if (!action.payload.isSaving && !action.payload.isError) {
        state.lastUpdate = dayjs().format("HH:mm");
      }
      state.isSaving = action.payload.isSaving;
      // disable the feature where auto saves releases adding rows lock
      // state.noSaves += 1;
    },
    setNoSaves: (state, action: PayloadAction<number>) => {
      state.noSaves = action.payload;
    },
    setActiveAutosave: (state, action: PayloadAction<boolean>) => {
      state.activeAutosave = action.payload;
    },
    suspendAutosave: state => {
      state.autosaveSuspendCount += 1;
    },
    resumeAutosave: state => {
      state.autosaveSuspendCount = Math.max(0, state.autosaveSuspendCount - 1);
    },
    setSilentEntryTransition: (state, action: PayloadAction<boolean>) => {
      state.silentEntryTransition = action.payload;
    },
    clearSilentEntryTransition: state => {
      state.silentEntryTransition = false;
    },
    setOpenedSaveModal: (state, action: PayloadAction<boolean>) => {
      state.openedSaveModal = action.payload;
    },
    setOpenedRefetchModal: (state, action: PayloadAction<boolean>) => {
      state.openedRefetchModal = action.payload;
    },
    setCurrent: (state, action: PayloadAction<string>) => {
      state.current = action.payload;
    },
    resetEntryLock: () => initialState,
    setReleaseEntryLock: (state, action: PayloadAction<number>) => {
      state.releaseEntryLock = action.payload;
    },
  },
});

export const {
  initEntryLockData,
  setEntryLockData,
  setAutoSaving,
  setActiveAutosave,
  suspendAutosave,
  resumeAutosave,
  setSilentEntryTransition,
  clearSilentEntryTransition,
  setOpenedSaveModal,
  setOpenedRefetchModal,
  setEditing,
  resetEntryLock,
  pushNewEntryActionsData,
  reassignNewChecklistActions,
  removeFlushedChecklistActions,
  setCurrent,
  setReleaseEntryLock,
  setNoSaves,
} = entrySlice.actions;

export default entrySlice.reducer;
